import { cogProtocol, setColorFunction } from '@geomatico/maplibre-cog-protocol'
import { interpolateRgb } from 'd3-interpolate'
import maplibregl from 'maplibre-gl'
import { BATHYMETRY_COG_URL, BATHYMETRY_DEPTH_COLORS } from '~/constants'
import { erosionRgba } from './erosionRamp'

maplibregl.addProtocol('cog', cogProtocol)

const TRANSPARENT = [0, 0, 0, 0]

type Rgb = [number, number, number]

/** Pre-build d3 interpolators for each adjacent pair of color stops (avoids per-pixel allocation) */
const bathymetryStops = BATHYMETRY_DEPTH_COLORS.map(([depth, color]) => ({
  depth,
  color,
}))

const bathymetryInterpolators = bathymetryStops.slice(0, -1).map((upper, i) => ({
  upper,
  lower: bathymetryStops[i + 1]!,
  interpolate: interpolateRgb(upper.color, bathymetryStops[i + 1]!.color),
}))

function parseRgbString(rgb: string): Rgb {
  const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (!match) return [0, 0, 0]
  return [+match[1]!, +match[2]!, +match[3]!]
}

function hexToRgb(hex: string): Rgb {
  const normalized = hex.replace('#', '')
  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)
  return [r, g, b]
}

function colorForBathymetryDepth(depth: number): Rgb {
  if (depth >= 0) {
    return [0, 0, 0]
  }

  const minDepth = bathymetryStops[bathymetryStops.length - 1]!.depth
  const maxDepth = bathymetryStops[0]!.depth
  const clampedDepth = Math.max(minDepth, Math.min(depth, maxDepth))

  for (const { upper, lower, interpolate } of bathymetryInterpolators) {
    if (clampedDepth <= upper.depth && clampedDepth >= lower.depth) {
      const range = upper.depth - lower.depth
      const t = range === 0 ? 0 : (upper.depth - clampedDepth) / range
      return parseRgbString(interpolate(t))
    }
  }

  return hexToRgb(bathymetryStops[bathymetryStops.length - 1]!.color)
}

// Color functions are registered at module load time to ensure they're available before any components render
// Note: COGs must be created with rio-cogeo --web-optimized for proper georeferencing

const BATHYMETRY_NODATA = -999

setColorFunction(BATHYMETRY_COG_URL, (pixel, color, _metadata) => {
  if (!pixel || pixel[0] === undefined || Number.isNaN(pixel[0])) {
    color.set(TRANSPARENT)
    return
  }

  const depth = pixel[0]

  // NoData value and land (>= 0) should be transparent
  if (depth === BATHYMETRY_NODATA || depth >= 0) {
    color.set(TRANSPARENT)
    return
  }

  const [r, g, b] = colorForBathymetryDepth(depth)
  color.set([r, g, b, 255])
})

const registeredSederoUrls = new Set<string>()

/**
 * Register a per-pixel colour function for one sedero COG. Idempotent, and safe to call
 * during render — the protocol keys renderers by exact URL and throws if a tile arrives
 * before its function is registered.
 */
export function registerSederoColorFunction(url: string): void {
  if (registeredSederoUrls.has(url)) return
  registeredSederoUrls.add(url)

  setColorFunction(url, (pixel, color) => {
    const change = pixel?.[0]
    // Nodata is declared NaN, but CogReader substitutes +Infinity for out-of-footprint
    // pixels, so a NaN-only test lets padding render as extreme deposition.
    if (change === undefined || !Number.isFinite(change)) {
      color.set(TRANSPARENT)
      return
    }
    color.set(erosionRgba(change))
  })
}
